import { handleFormChange } from "@/utils/formChange";
import { FC, useEffect, useState } from "react";
import { Label } from "../ui/label";
import { BASE_URL } from "@/config";
import { Users, Euro, Baby } from "lucide-react";
import { BaseInput, BaseSelect, BaseTextArea } from "../input/CustomInputs";
import DatePicker from "../input/DatePicker";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { generateAvailableTimeSlots } from "@/utils/generateTimeSlots";
import axiosInstance from "@/utils/axiosInstance";
import { useUserContext } from "@/context/UserContext";
import { subYears } from "date-fns";

interface Props {
  formProp: any;
  setFormProp: Function;
  finalPrice: number | string;
  setFinalPrice: Function;
  tourId?: number;
  loading?: boolean;
}

const OrderFormCommon: FC<Props> = ({
  formProp,
  setFormProp,
  finalPrice,
  setFinalPrice,
  tourId,
}) => {
  const { decodedToken } = useUserContext()
  const [prices, setPrices] = useState<{ group_size: number; price: number }[]>(
    [],
  );
  const [discountPrice, setDiscountPrice] = useState(0);
  const [totalPrice, setTotalPrice] = useState(0);

  useEffect(() => {
    const getTourPrices = async () => {
      const id = tourId || formProp.tourId;
      if (!id) return;
      try {
        const res = await axiosInstance.get(
          `/tour-pricing/tour/${id}`,
        );
        if (res.data) {
          setPrices(res.data);
        }
      } catch (error) {}
    };

    getTourPrices();
  }, [tourId, formProp.tourId]);

  useEffect(() => {
    const totalPassengers =
      Number(formProp.how_many_adults) + Number(formProp.how_many_children);

    let priceFinal = prices.find(
      (item) => totalPassengers === item.group_size,
    )?.price || 0;
    setTotalPrice(priceFinal);
    if (formProp.how_many_children && totalPassengers > 3) {
      setDiscountPrice(Math.floor(priceFinal * 0.1 * 100) / 100);
      priceFinal *= 0.9;
    } else setDiscountPrice(0);
    setFinalPrice(Math.floor(priceFinal * 100) / 100);
  }, [formProp.how_many_adults, formProp.how_many_children, prices]);

  return (
    <>
      <div className="grid sm:grid-cols-2 gap-5">
        <BaseInput
          id="fullName"
          label="Full Name"
          value={formProp.fullName}
          setProp={setFormProp}
          placeholder="Enter full name"
        />
        <BaseInput
          id="client_phone"
          label="Phone number"
          value={formProp.client_phone}
          setProp={setFormProp}
          type="number"
          placeholder="Enter client phone number"
        />
        <div
          className={`w-full ${decodedToken?.permissions.user_role !== "owner" && "sm:col-span-2"}`}
        >
          <BaseInput
            id="email"
            type="email"
            label="Email"
            value={formProp.email}
            setProp={setFormProp}
            placeholder="Enter email"
          />
        </div>
        {decodedToken?.permissions.user_role === "owner" && (
          <BaseSelect
            id="tourId"
            value={formProp.tourId}
            setValue={setFormProp}
            dataProp="name"
            resource="tours"
            label="Select tour"
            required={true}
          />
        )}
        <div className="w-full">
          <Label htmlFor="date" className="text-right mb-2">
            Date *
          </Label>
          <DatePicker
            value={formProp.date}
            setValue={(e: any) => handleFormChange(e, setFormProp)}
            minDate={decodedToken?.permissions.user_role === 'owner' ? subYears(new Date(), 2) : new Date()}
          />
        </div>
        <div className="w-full">
          <Label htmlFor="time" className="text-right mb-2">
            Time *
          </Label>
          <Select
            value={formProp.time}
            onValueChange={(value) =>
              setFormProp((prev: any) => ({ ...prev, time: value }))
            }
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select time" />
            </SelectTrigger>
            <SelectContent>
              {generateAvailableTimeSlots(formProp.date).map((time) => (
                <SelectItem key={time} value={time}>
                  {time}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <BaseInput
          id="how_many_adults"
          label="Number of adults"
          type="number"
          value={formProp.how_many_adults}
          setProp={setFormProp}
          placeholder="Number of adults"
        />
        <BaseInput
          id="how_many_children"
          label="Number of children"
          type="number"
          value={formProp.how_many_children}
          setProp={setFormProp}
          required={false}
          placeholder="Number of children"
        />
        <div className="w-full sm:col-span-2">
          <BaseTextArea
            id="additional_notes"
            label="Notes"
            value={formProp.additional_notes}
            setProp={setFormProp}
          />
        </div>
      </div>
      <div className="mt-5">
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <Users className="h-6 w-6" />
            <div>
              <p className="text-sm font-medium">Price</p>
              <p className="text-lg font-bold">€{totalPrice || 0}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Baby className="h-6 w-6" />
            <div>
              <p className="text-sm font-medium">Discount</p>
              <p className="text-lg font-bold">€{discountPrice || 0}</p>
            </div>
          </div>
          <div className="flex items-center gap-3 border-t pt-4">
            <Euro className="h-6 w-6" />
            <div>
              <p className="text-sm font-medium">Total Price</p>
              <p className="text-2xl font-bold">€{finalPrice || 0}</p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default OrderFormCommon;
